import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Tv, CheckCircle, Image as ImageIcon, LockKeyhole, X, XCircle } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, Role } from "@/contexts/AuthContext";
import AuthModal from "@/components/AuthModal";
import { useEmissionsStore } from "@/data/emissionsStore";

const labels = {
  fr: {
    title: "Proposer une émission",
    subtitle: "Proposez vos programmes politiques ou débats citoyens pour publication.",
    authTitle: "Espace Dédié Requis",
    authDesc: "Vous devez être connecté en tant que support média pour proposer une émission. Veuillez choisir votre catégorie :",
    mediaSection: "Informations du Média",
    channelName: "Nom de la chaîne (Radio/TV)",
    contactName: "Contact principal",
    contactEmail: "Email de contact",
    contactPhone: "Téléphone",
    eventSection: "Informations de l'émission",
    eventTitle: "Titre de l'émission",
    eventDescription: "Description détaillée",
    eventImage: "Affiche ou image du programme",
    eventDate: "Date de diffusion",
    eventTime: "Heure de diffusion",
    eventType: "Type de média",
    selectType: "Sélectionnez un type",
    types: { tv: "Télévision", radio: "Radio", web: "Web TV" },
    periodicity: "Périodicité",
    selectPeriodicity: "Sélectionnez",
    periodicities: { quotidien: "Quotidien", hebdo: "Hebdomadaire", mensuel: "Mensuel", ponctuel: "Spécial / Ponctuel" },
    declaration: "Je certifie que cette publication respecte la charte éthique de la plateforme.",
    submit: "Soumettre pour validation",
    successTitle: "Émission soumise !",
    successMessage: "Votre proposition est bien envoyée pour modération. Vous pouvez la suivre sur votre espace dédié.",
    back: "Retour",
    required: "Ce champ est obligatoire",
    externalLink: "Lien de rediffusion ou direct (facultatif)",
    externalLinkDesc: "Lien YouTube, site web, etc.",
    importImage: "Importer l'image",
    imageLimit: "L'image est trop grande (max 2Mo).",
    authTitleSwitch: "Changement de compte requis",
    authTitleReq: "Espace Dédié Requis",
    authDescSwitch: "Pour publier une émission, le changement de type d'inscription est requis (Espace Dédié). Veuillez choisir votre catégorie :",
    authDescReq: "Vous devez être connecté en tant que support média pour proposer une émission. Veuillez choisir votre catégorie :",
    selectMedia: "Sélectionnez...",
    radioOption: "📻 Radio",
    tvOption: "📺 TV",
    noneOption: "Aucun",
    validateBtn: "Valider",
    homeBtn: "Retour",
    alreadyRegistered: "Déjà inscrit ? Se connecter",
    imageFormats: "SVG, PNG, JPG (MAX. 2Mo)",
  },
  ar: {
    title: "اقتراح برنامج",
    subtitle: "اقترحوا برامجكم السياسية أو النقاشات المواطنة للنشر.",
    authTitle: "فضاء مخصص مطلوب",
    authDesc: "يجب أن تكون متصلاً كداعم إعلامي لاقتراح برنامج. يرجى اختيار فئتك:",
    mediaSection: "معلومات الوسيط الإعلامي",
    channelName: "اسم القناة (راديو/تلفزيون)",
    contactName: "جهة الاتصال الرئيسية",
    contactEmail: "البريد الإلكتروني",
    contactPhone: "الهاتف",
    eventSection: "معلومات البرنامج",
    eventTitle: "عنوان البرنامج",
    eventDescription: "وصف مفصل",
    eventImage: "صورة أو ملصق البرنامج",
    eventDate: "تاريخ البث",
    eventTime: "وقت البث",
    eventType: "نوع الوسيط",
    selectType: "اختر نوعاً",
    types: { tv: "تلفزيون", radio: "راديو", web: "تلفزيون الويب" },
    periodicity: "الدورية",
    selectPeriodicity: "اختر",
    periodicities: { quotidien: "يومي", hebdo: "أسبوعي", mensuel: "شهري", ponctuel: "خاص" },
    declaration: "أشهد بأن هذا المنشور يحترم الميثاق الأخلاقي للمنصة.",
    submit: "إرسال للمصادقة",
    successTitle: "تم الإرسال!",
    successMessage: "تم إرسال اقتراحكم للمراجعة.",
    back: "رجوع",
    required: "هذا الحقل إلزامي",
    externalLink: "رابط البث أو الإعادة (اختياري)",
    externalLinkDesc: "رابط يوتيوب أو الموقع، الخ",
    importImage: "استيراد الصورة",
    imageLimit: "الصورة كبيرة جداً (الحد الأقصى 2 ميغابايت).",
    authTitleSwitch: "مطلوب تغيير الحساب",
    authTitleReq: "فضاء مخصص مطلوب",
    authDescSwitch: "لنشر برنامج، يتطلب الأمر تغيير نوع التسجيل (فضاء مخصص). يرجى اختيار فئتك:",
    authDescReq: "يجب أن تكون متصلاً كداعم إعلامي لاقتراح برنامج. يرجى اختيار فئتك:",
    selectMedia: "اختر...",
    radioOption: "📻 راديو",
    tvOption: "📺 تلفزيون",
    noneOption: "لا شيء",
    validateBtn: "تأكيد",
    homeBtn: "رجوع",
    alreadyRegistered: "مسجل مسبقا؟ تسجيل الدخول",
    imageFormats: "SVG، PNG، JPG (الحد الأقصى 2 ميغابايت)",
  },
  zgh: {
    title: "ⵙⵉⴷⴼ ⴰⵖⴰⵡⴰⵙ",
    subtitle: "ⵙⵉⴷⴼⵜ ⵉⵖⴰⵡⴰⵙⵏ ⵏⵏⵓⵏ ⵉ ⵓⵙⴼⵇⵇⵔ.",
    authTitle: "ⴰⵙⴰⵢⵔⴰⵔ ⵉⵥⵍⵉⵏ ⵉⵅⵚⵚⴰ",
    authDesc: "ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⴽⵛⵎⵜ ⵙ ⵉⵙⵎ ⵏ ⵓⵎⵉⴷⵢⴰ. ⵙⵜⵉ ⴰⵏⴰⵡ ⵏⵏⴽ:",
    mediaSection: "ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⵎⵉⴷⵢⴰ",
    channelName: "ⵉⵙⵎ ⵏ ⵜⴱⴰⴷⵓⵜ",
    contactName: "ⵉⵙⵎ ⵏ ⵓⵎⵙⵓⵍ",
    contactEmail: "ⵉⵎⴰⵢⵍ",
    contactPhone: "ⵜⵉⵍⵉⴼⵓⵏ",
    eventSection: "ⵉⵏⵖⵎⵉⵙⵏ ⵏ ⵓⵖⴰⵡⴰⵙ",
    eventTitle: "ⴰⵣⵡⵍ ⵏ ⵓⵖⴰⵡⴰⵙ",
    eventDescription: "ⴰⴳⵍⴰⵎ",
    eventImage: "ⵜⴰⵡⵍⴰⴼⵜ",
    eventDate: "ⴰⵙⴰⴽⵓⴷ",
    eventTime: "ⵜⴰⵙⵔⴰⴳⵜ",
    eventType: "ⴰⵏⴰⵡ",
    selectType: "ⵙⵜⵉ ⴰⵏⴰⵡ",
    types: { tv: "ⵜⵉⵍⵉⴼⵉⵣⵢⵓⵏ", radio: "ⵕⵕⴰⴷⵢⵓ", web: "ⵡⵉⴱ" },
    periodicity: "ⵜⴰⵎⵀⵍⴰ",
    selectPeriodicity: "ⵙⵜⵉ",
    periodicities: { quotidien: "ⴰⵙⵙⴰⵏ", hebdo: "ⴰⵏⵎⵍⴰⵙⵙ", mensuel: "ⴰⵢⵢⵓⵔⴰⵏ", ponctuel: "ⵉⵥⵍⵉⵏ" },
    declaration: "ⵙⴼⵇⵇⵔⵖ ⵎⴰⵙ ⵓⵔⴰⵔⵏ ⵏⵏⴰ ⵙⵔⵙⵏ ⵉⵙⵎⵖⵓⵔⴰⵏ ⵏ ⵓⵙⴰⵢⵔⴰⵔ.",
    submit: "ⵙⵉⴷⴼ",
    successTitle: "ⵉⵜⵜⵓⵙⵉⴷⴼ!",
    successMessage: "ⵉⵜⵜⵓⵙⵉⴷⴼ ⵓⵙⵓⵎⵔ ⵏⵏⴽ.",
    back: "ⵓⵖⴰⵍ",
    required: "ⴰⵢⴰ ⵉⵅⵚⵚⴰ",
    externalLink: "ⴰⵙⴰⵖⵓⵍ (ⴰⵎⵙⵜⴰⵢ)",
    externalLinkDesc: "ⵢⵓⵜⵢⵓⴱ ⵏⵖ ⵡⵉⴱ...",
    importImage: "ⵙⵉⴷⴼ ⵜⴰⵡⵍⴰⴼⵜ",
    imageLimit: "ⵜⴰⵡⵍⴰⴼⵜ ⵜⵎⵇⵇⵓⵔ ⴱⴰⵀⵔⴰ (ⴰⴼⵍⵍⴰ 2Mo).",
    authTitleSwitch: "ⴰⵙⵏⴼⵍ ⵏ ⵓⵎⵉⴹⴰⵏ ⵉⵅⵚⵚⴰ",
    authTitleReq: "ⴰⵙⴰⵢⵔⴰⵔ ⵉⵥⵍⵉⵏ ⵉⵅⵚⵚⴰ",
    authDescSwitch: "ⵉ ⵓⵙⴼⵇⵇⵔ ⵏ ⵓⵖⴰⵡⴰⵙ, ⵉⵅⵚⵚⴰ ⴰⵙⵏⴼⵍ ⵏ ⵡⴰⵏⴰⵡ ⵏ ⵓⵣⵎⵎⴻⵎ. ⵙⵜⵉ ⴰⵏⴰⵡ ⵏⵏⴽ:",
    authDescReq: "ⵉⵅⵚⵚⴰ ⴰⴷ ⵜⴽⵛⵎⵜ ⵙ ⵉⵙⵎ ⵏ ⵓⵎⵉⴷⵢⴰ. ⵙⵜⵉ ⴰⵏⴰⵡ ⵏⵏⴽ:",
    selectMedia: "ⵙⵜⵉ...",
    radioOption: "📻 ⵕⵕⴰⴷⵢⵓ",
    tvOption: "📺 ⵜⵉⵍⵉⴼⵉⵣⵢⵓⵏ",
    noneOption: "ⵡⴰⵍⵓ",
    validateBtn: "ⵙⵖⵣⵏ",
    homeBtn: "ⴰⵖⵓⵍ",
    alreadyRegistered: "ⵜⵣⵎⵎⴻⵎⴷ ⵢⴰⴷ? ⴽⵛⵎ",
    imageFormats: "SVG, PNG, JPG (ⴰⴼⵍⵍⴰ 2Mo)",
  }
};

const ProposerEmissionPage = () => {
  const { lang, isRtl } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<string>("");
  const [authModalConfig, setAuthModalConfig] = useState({ tab: "register" as any, regType: "dedie" as any, role: "MEDIA" as any });
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  const [submitted, setSubmitted] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    channelName: "",
    contactName: "",
    contactPhone: "",
    contactEmail: "",
    title: "",
    description: "",
    date: "",
    time: "",
    type: "tv",
    periodicity: "hebdo",
    imageUrl: "",
    externalLink: ""
  });

  useEffect(() => {
    if (user && user.role === 'MEDIA') {
      setFormData(prev => ({
        ...prev,
        channelName: user.channelName || user.name || "",
        contactName: user.name || "",
        contactEmail: user.email || "",
        contactPhone: user.phone || ""
      }));
    }
  }, [user]);

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container pt-24 pb-16 flex items-center justify-center min-h-[80vh]">
          <Card className="max-w-md w-full text-center shadow-elevated">
            <CardContent className="p-8">
              <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
              <h2 className="font-display font-bold text-2xl text-foreground mb-3">{l.successTitle}</h2>
              <p className="text-muted-foreground font-body mb-6">{l.successMessage}</p>
              <Button onClick={() => user ? navigate('/dashboard') : navigate('/')}>{l.back}</Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const handleSave = async () => {
    if (!formData.title || !formData.date || !formData.time || !formData.channelName) {
      alert(l.required);
      return;
    }

    setIsSubmitting(true);

    const newEmission = {
      channel: formData.channelName,
      frTitle: formData.title,
      arTitle: formData.title,
      zghTitle: formData.title,
      description_fr: formData.description,
      description_ar: formData.description,
      description_zgh: formData.description,
      dateIso: `${formData.date}T${formData.time}:00`,
      time: formData.time,
      dateFormatted_fr: new Date(formData.date).toLocaleDateString('fr-FR'),
      dateFormatted_ar: new Date(formData.date).toLocaleDateString('ar-MA'),
      dateFormatted_zgh: new Date(formData.date).toLocaleDateString('fr-FR'),
      type: formData.type as "tv" | "radio" | "web",
      periodicity: formData.periodicity as any,
      posterUrl: formData.imageUrl,
      liveUrl: formData.externalLink,
      published: false,
      status: "pending" as const,
    };

    try {
      const store = useEmissionsStore.getState();
      const saved = await store.saveEmission(newEmission);
      
      if (saved) {
        // Also trigger custom event if needed
        window.dispatchEvent(new Event('emissionsUpdated'));
        setSubmitted(true);
      } else {
        alert("Erreur lors de la soumission de l'émission.");
      }
    } catch (err) {
      console.error(err);
      alert("Une erreur est survenue.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        alert(l.imageLimit);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, imageUrl: reader.result as string });
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="min-h-screen bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container pt-24 pb-16 max-w-3xl">
        <Button variant="ghost" className="mb-6 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors text-muted-foreground" onClick={() => navigate(-1)}>
          <ArrowLeft className={`w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180`} />{l.back}
        </Button>

        <div className="text-center mb-8">
          <Tv className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-display font-bold text-3xl text-foreground mb-2">{l.title}</h1>
          <p className="text-muted-foreground font-body">{l.subtitle}</p>
        </div>

        {(!isAuthenticated || user?.role !== 'MEDIA') ? (
          <div className="flex flex-col items-center justify-center p-8 bg-card rounded-2xl border border-border shadow-md max-w-lg mx-auto animate-in fade-in zoom-in-95 mt-8">
             <LockKeyhole className="w-16 h-16 text-muted-foreground mb-4 opacity-80" />
             <h3 className="text-2xl font-display font-bold text-foreground mb-3 text-center">
               {user ? l.authTitleSwitch : l.authTitleReq}
             </h3>
             <p className="text-muted-foreground font-body mb-6 text-center text-sm">
               {user ? l.authDescSwitch : l.authDescReq}
             </p>
             
             <div className="w-full space-y-4 mb-8">
               <Select value={selectedRole} onValueChange={(v) => setSelectedRole(v)}>
                 <SelectTrigger className={`w-full h-12 font-body ${isRtl ? "flex-row-reverse" : ""}`}>
                   <SelectValue placeholder={l.selectMedia} />
                 </SelectTrigger>
                 <SelectContent className="font-body">
                   <SelectItem value="RADIO" className={isRtl ? "text-right" : ""}>{l.radioOption}</SelectItem>
                   <SelectItem value="TV" className={isRtl ? "text-right" : ""}>{l.tvOption}</SelectItem>
                   <SelectItem value="NONE" className={isRtl ? "text-right" : ""}>{l.noneOption}</SelectItem>
                 </SelectContent>
               </Select>
             </div>

             <div className="flex flex-col sm:flex-row gap-3 w-full mb-6">
               <Button 
                 size="lg" 
                 className="flex-1 font-body font-bold bg-primary hover:bg-primary/90"
                 disabled={!selectedRole}
                 onClick={() => {
                   if (selectedRole === "NONE") {
                     navigate("/");
                   } else {
                     if (user) logout(); 
                     setAuthModalConfig({ tab: "register", regType: "dedie", role: "MEDIA" });
                     setIsAuthModalOpen(true);
                   }
                 }}
               >
                 {l.validateBtn}
               </Button>
               <Button variant="outline" size="lg" className="flex-1 font-body" onClick={() => navigate(-1)}>
                 {l.homeBtn}
               </Button>
             </div>
             
             <div className="w-full pt-4 border-t border-border/50 text-center">
               <Button 
                 variant="link" 
                 className="text-primary font-bold font-body"
                 onClick={() => {
                   setAuthModalConfig({ tab: "login", regType: "citoyen" });
                   setIsAuthModalOpen(true);
                 }}
               >
                 {l.alreadyRegistered}
               </Button>
             </div>
          </div>
        ) : (
          <form onSubmit={(e) => e.preventDefault()} className="space-y-8">
            <Card className="shadow-card">
              <CardHeader><CardTitle className="text-lg font-display">{l.mediaSection}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body">{l.channelName} *</Label>
                  <Input required className="mt-1" value={formData.channelName} onChange={(e) => setFormData({...formData, channelName: e.target.value})} disabled={!!user?.channelName} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <Label className="font-body">{l.contactName} *</Label>
                    <Input required className="mt-1" value={formData.contactName} onChange={(e) => setFormData({...formData, contactName: e.target.value})} />
                  </div>
                  <div>
                    <Label className="font-body">{l.contactPhone}</Label>
                    <Input type="tel" className="mt-1" value={formData.contactPhone} onChange={(e) => setFormData({...formData, contactPhone: e.target.value})} />
                  </div>
                </div>
                <div>
                  <Label className="font-body">{l.contactEmail} *</Label>
                  <Input type="email" required className="mt-1" value={formData.contactEmail} onChange={(e) => setFormData({...formData, contactEmail: e.target.value})} />
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardHeader><CardTitle className="text-lg font-display">{l.eventSection}</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="font-body">{l.eventTitle} *</Label>
                  <Input required className="mt-1" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body">{l.eventDescription}</Label>
                  <Textarea className="mt-1" rows={4} value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body">{l.externalLink}</Label>
                  <p className="text-xs text-muted-foreground mb-1">{l.externalLinkDesc}</p>
                  <Input type="url" placeholder="https://..." className="mt-1" value={formData.externalLink} onChange={(e) => setFormData({...formData, externalLink: e.target.value})} />
                </div>
                <div>
                  <Label className="font-body mb-2 block">{l.eventImage}</Label>
                  {!formData.imageUrl ? (
                    <div className="flex items-center justify-center w-full">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors border-border">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6 text-muted-foreground">
                          <ImageIcon className="w-8 h-8 mb-3 opacity-50" />
                          <p className="mb-2 text-sm font-body"><span className="font-semibold">{l.importImage}</span></p>
                          <p className="text-xs">{l.imageFormats}</p>
                        </div>
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                      </label>
                    </div>
                  ) : (
                    <div className="relative w-full rounded-lg overflow-hidden border border-border group">
                      <img src={formData.imageUrl} alt="Prévisualisation" className="w-full h-auto object-cover max-h-64" />
                      <Button type="button" variant="destructive" size="icon" className="absolute top-2 right-2 w-8 h-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setFormData({ ...formData, imageUrl: "" })}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body">{l.eventDate} *</Label>
                    <Input type="date" required className="mt-1" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} />
                  </div>
                  <div>
                    <Label className="font-body">{l.eventTime} *</Label>
                    <Input type="time" required className="mt-1" value={formData.time} onChange={(e) => setFormData({...formData, time: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-body">{l.eventType} *</Label>
                    <Select required value={formData.type} onValueChange={(val) => setFormData({...formData, type: val})}>
                      <SelectTrigger className={`mt-1 ${isRtl ? "flex-row-reverse" : ""}`}><SelectValue placeholder={l.selectType} /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(l.types).map(([k, v]) => (<SelectItem key={k} value={k} className={isRtl ? "text-right" : ""}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="font-body">{l.periodicity} *</Label>
                    <Select required value={formData.periodicity} onValueChange={(val) => setFormData({...formData, periodicity: val})}>
                      <SelectTrigger className={`mt-1 ${isRtl ? "flex-row-reverse" : ""}`}><SelectValue placeholder={l.selectPeriodicity} /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(l.periodicities).map(([k, v]) => (<SelectItem key={k} value={k} className={isRtl ? "text-right" : ""}>{v}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="flex items-start gap-3 px-1">
              <Checkbox id="declaration" checked={accepted} onCheckedChange={(v) => setAccepted(v === true)} />
              <Label htmlFor="declaration" className="font-body text-sm text-muted-foreground leading-relaxed cursor-pointer">
                {l.declaration}
              </Label>
            </div>

            <div className="pt-6 border-t border-border space-y-4">
              <div className="flex gap-4">
                <Button type="button" size="lg" className="w-full font-body font-semibold" onClick={handleSave} disabled={!accepted || isSubmitting}>
                  {isSubmitting ? "Envoi en cours..." : l.submit}
                </Button>
              </div>
            </div>
          </form>
        )}
      </div>
      <AuthModal 
        isOpen={isAuthModalOpen} 
        onClose={() => setIsAuthModalOpen(false)} 
        initialTab={authModalConfig.tab}
        initialRegistrationType={authModalConfig.regType}
        initialRole={authModalConfig.role}
        lockSelection={true}
      />
      <Footer />
    </div>
  );
};

export default ProposerEmissionPage;
